<template>
  <template v-if="showingButton">
    <Button ref="popoverAnchor" text size="small" severity="secondary" @click="showPopover()">
      Auxiliaries order
      <i class="pi pi-angle-down"></i>
    </Button>
    <Popover ref="popover" v-model:showingPopover="showingPopover" :popoverAnchor="popoverAnchor?.$el">
      <h3 style="padding: 15px; padding-bottom: 30px; text-align: center;">
        Auxiliaries order
      </h3>
      <ul style="display: flex; flex-direction: column; align-items: stretch; gap: 15px; padding-left: 30px; padding-right: 30px;">
        <TransitionGroup>
          <li v-for="(auxiliaryName, index) in auxiliariesOrder" :key="auxiliaryName" style="display: flex; align-items: stretch; gap: 15px;">
            <div style="flex-grow: 1;"></div>
            <div style="display: flex; align-items: center;">
              {{ auxiliaryName }}
            </div>
            <Button text severity="secondary" style="width: 55px; justify-content: center;" @click="onClickOrdinal($event, index)">
              <span v-html="ordinalLabel(index)"></span>
            </Button>
            <template v-if="showingAnyRemove">
              <Button v-if="showingRemove(auxiliaryName)" text severity="secondary" style="width: 55px;" @click="removeAuxiliary(auxiliaryName)">
                <i class="pi pi-trash"></i>
              </Button>
              <div v-else style="width: 55px;"></div>
            </template>
          </li>
        </TransitionGroup>
      </ul>
      <Menu ref="ordinalMenu" popup :model="ordinalMenuItems">
        <template #item="{item, props}">
          <a v-ripple v-bind="props.action">
            <span v-html="item.label"></span>
          </a>
        </template>
      </Menu>
      <div style="display: flex; gap: 15px; margin-top: 30px;">
        <Button style="flex-grow: 1; flex-basis: 0; justify-content: center;" @click="ok()">
          Ok
        </Button>
        <Button severity="secondary" style="flex-grow: 1; flex-basis: 0; justify-content: center;" @click="cancel()">
          Cancel
        </Button>
      </div>
    </Popover>
  </template>
</template>

<script setup>
import store from "./store.js";
import { computed, ref } from "vue";
import { arrayMove, sortBy, unique } from "./utility.js";
import { ordinalLabel } from "./appUtility.js";
import Popover from "./Popover.vue";

let props = defineProps([
  "tag"
]);

function getAllSystemAuxiliaries(sessions) {
  return sessions.flatMap(session => session.system.auxiliaries.children);
}

function getAllBinAuxiliaries(sessions) {
  return sessions.flatMap(session => session.system.bins.children.flatMap(bin => bin.auxiliaries.children));
}

let showingButton = computed(() => {
  let system = store.sessions[0].system;
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let isSystemAuxiliary = props.tag.$path().startsWith("auxiliaries[");
  let auxiliaries = isSystemAuxiliary ? getAllSystemAuxiliaries(connectedSessions) : getAllBinAuxiliaries(connectedSessions);
  let auxiliariesOrder = isSystemAuxiliary ? system.systemAuxiliariesOrder.value : system.auxiliariesOrder.value;
  let isAuxiliaryAggregate = props.tag.$parents().some(parent => auxiliaries.includes(parent));
  return (
    store.showAdvanced &&
    isAuxiliaryAggregate &&
    store.sessions[0].system.auxiliariesOrder.value.length > 1
  );
});

let popover = ref();
let popoverAnchor = ref();
let showingPopover = ref(false);

function showPopover() {
  showingPopover.value = true;
  auxiliariesOrder.value = initAuxiliariesOrder();
}

let allAuxiliaries = computed(() => {
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  let isSystemAuxiliary = props.tag.$path().startsWith("auxiliaries[");
  let auxiliaries = isSystemAuxiliary ? getAllSystemAuxiliaries(connectedSessions) : getAllBinAuxiliaries(connectedSessions);
  return auxiliaries;
});

let auxiliariesOrder = ref(null);

function initAuxiliariesOrder() {
  let system = store.sessions[0].system;
  let isSystemAuxiliary = props.tag.$path().startsWith("auxiliaries[");
  let auxiliariesOrder = isSystemAuxiliary ? system.systemAuxiliariesOrder.value : system.auxiliariesOrder.value;
  let auxiliaryNames = unique([
    ... allAuxiliaries.value.map(auxiliary => auxiliary.name.value),
    ... auxiliariesOrder
  ]);
  let auxiliaryNamesSorted = partialSort(auxiliaryNames, auxiliariesOrder);
  return auxiliaryNamesSorted;
}

function showingRemove(auxiliaryName) {
  return !allAuxiliaries.value.some(auxiliary => auxiliaryName == auxiliary.name.value);
}

let showingAnyRemove = computed(() => {
  return auxiliariesOrder.value.some(auxiliaryName => showingRemove(auxiliaryName));
});

async function removeAuxiliary(auxiliaryName) {
  auxiliariesOrder.value = auxiliariesOrder.value.filter(name => name != auxiliaryName);
  await popover.value?.reposition();
}

function partialSort(items, order) {
  let result = items.map(item => {
    let hasSpecifiedOrder = order.includes(item);
    return hasSpecifiedOrder ? null : item;
  });
  order.forEach(item => {
    let index = result.indexOf(null);
    result[index] = item;
  });
  return result;
}

let ordinalMenu = ref();
let ordinalMenuItems = ref([]);
function onClickOrdinal(event, auxiliaryIndex) {
  if (auxiliariesOrder.value.length == 2) {
    arrayMove(auxiliariesOrder.value, 0, 1);
  }
  else {
    ordinalMenuItems.value = auxiliariesOrder.value.map((auxiliaryName, index) => {
      return {
        label: ordinalLabel(index),
        command: () => arrayMove(auxiliariesOrder.value, auxiliaryIndex, index)
      };
    });
    ordinalMenu.value.toggle(event);
  }
}

function ok() {
  showingPopover.value = false;
  let connectedSessions = store.sessions.filter(session => session.isConnected);
  connectedSessions.forEach(session => {
    let system = session.system;
    session.changeSettings([
      { handle: system.auxiliariesOrder.handle, value: auxiliariesOrder.value }
    ]);
  });
}

function cancel() {
  showingPopover.value = false;
}
</script>

<style scoped>
.v-move,
.v-enter-active,
.v-leave-active,
.p-inputtext {
  transition: all 0.25s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
.v-leave-active {
  position: absolute;
}
</style>